/**
 * ExecDIB — Landing hub for the Defense Industrial Base section.
 *
 * Shows action cards for Explore, Dashboard, and an overview of what the
 * DIB section offers. Mirrors ExecHome layout but with state-government-
 * focused messaging.
 */
import { useNavigate } from 'react-router-dom'
import {
  Globe, Compass, LayoutDashboard, ArrowRight,
  Map as MapIcon, TrendingUp, BarChart3,
} from 'lucide-react'

interface ActionCard {
  id: string
  icon: React.ReactNode
  title: string
  description: string
  color: string
  navigateTo: string
}

const ACTIONS: ActionCard[] = [
  {
    id: 'explore',
    icon: <Compass size={20} />,
    title: 'Explore DIB Data',
    description: 'Interactively explore defense spend by state, congressional district, industry, and more.',
    color: 'from-blue-500 to-cyan-600',
    navigateTo: '/exec/dib/explore',
  },
  {
    id: 'dashboard',
    icon: <LayoutDashboard size={20} />,
    title: 'DIB Dashboard',
    description: 'Configure and view widgets showing your state\u2019s defense industrial base metrics.',
    color: 'from-emerald-500 to-teal-600',
    navigateTo: '/exec/dib/dashboard',
  },
]

const HIGHLIGHT_ITEMS = [
  { icon: <MapIcon size={16} />, label: 'State & CD choropleth maps' },
  { icon: <TrendingUp size={16} />, label: 'Industry trends over time' },
  { icon: <BarChart3 size={16} />, label: 'State rankings by PSC' },
]

export default function ExecDIB() {
  const navigate = useNavigate()

  return (
    <div className="h-full flex flex-col bg-gradient-to-br from-slate-50 via-white to-blue-50 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900">
      <div className="px-8 pt-8 pb-4 shrink-0">
        <div className="max-w-3xl mx-auto text-center">
          <div className="flex items-center justify-center gap-3 mb-2">
            <Globe size={32} className="text-blue-600" />
            <h1 className="text-3xl font-bold text-foreground">Defense Industrial Base</h1>
          </div>
          <p className="text-sm text-muted-foreground max-w-xl mx-auto">
            Understand how defense dollars flow to your state. Visualize spend by congressional
            district, industry trends, and compare across states.
          </p>
        </div>
      </div>

      <div className="flex-1 overflow-auto px-8 pt-4 pb-8">
        <div className="max-w-3xl mx-auto space-y-6">
          {/* What you can see */}
          <div className="flex items-center justify-center gap-6 py-3">
            {HIGHLIGHT_ITEMS.map((item) => (
              <div key={item.label} className="flex items-center gap-2 text-xs text-muted-foreground">
                <span className="text-blue-500">{item.icon}</span>
                {item.label}
              </div>
            ))}
          </div>

          {/* Action cards */}
          <div className="grid grid-cols-2 gap-4">
            {ACTIONS.map((action) => (
              <button
                key={action.id}
                onClick={() => navigate(action.navigateTo)}
                className="text-left p-5 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 hover:shadow-md hover:border-gray-300 dark:hover:border-gray-600 transition-all group"
              >
                <div className={`inline-flex p-2.5 rounded-lg bg-gradient-to-br ${action.color} text-white mb-3`}>
                  {action.icon}
                </div>
                <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-1 group-hover:text-blue-600 transition-colors">
                  {action.title}
                </h3>
                <p className="text-xs text-gray-500 leading-relaxed">
                  {action.description}
                </p>
                <div className="flex items-center gap-1 mt-3 text-xs text-blue-600 opacity-0 group-hover:opacity-100 transition-opacity">
                  Open <ArrowRight size={12} />
                </div>
              </button>
            ))}
          </div>

          {/* Coming soon banner */}
          <div className="rounded-lg border border-dashed border-gray-300 dark:border-gray-700 p-4 text-center text-xs text-muted-foreground">
            <span className="font-medium">Coming soon:</span>{' '}
            DIB employees vs state workforce utilization — pending employee-data enrichment.
          </div>
        </div>
      </div>
    </div>
  )
}
